import { Injectable, Inject } from '@angular/core';
import { Action } from '@ngrx/store';
import { CheckoutService } from '@modules/checkout-store/services';
import { ImageUpload } from '@modules/checkout-store/types/image-upload.interface';
import { LOGGER } from '@modules/core';
import { ApiError } from '@modules/ticket-fares-store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { LoggingService } from '@nx-ng/types';
import { catchError, mergeMap } from 'rxjs/operators';
import { ImageActions } from '../../actions';
import { ApplicantImageUploadOperationResponse } from '../../client/src';

@Injectable()
export class ImageEffects {
  imageUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImageActions.checkoutAPI_uploadImage),
      mergeMap((action: { image: ImageUpload } & Action) => {
        const { image } = action;
        return this.checkoutService.applicantImageUpload(image).pipe(
          mergeMap((response: ApplicantImageUploadOperationResponse) => {
            return [
              ImageActions.checkoutAPI_loadImage({
                basketId: image.basketId,
                itemId: image.itemId,
                quantityIndex: image.quantityIndex
              }),
              ImageActions.checkoutAPI_uploadImageSuccess({
                basketId: image.basketId,
                itemId: image.itemId,
                quantityIndex: image.quantityIndex,
                response,
              }),
            ];
          }),
          catchError((error) => {
            const apiError: ApiError | string =
              error?.response?.parsedBody || error;

            this.logger.logApiError(
              {
                apiError,
              },
              error
            );

            return of(
              ImageActions.checkoutAPI_uploadImageFailure({
                error: apiError,
                basketId: image.basketId,
                itemId: image.itemId,
                quantityIndex: image.quantityIndex,
                retryAction: action,
              })
            );
          })
        );
      })
    )
  );

  loadImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImageActions.checkoutAPI_loadImage),
      mergeMap((action: { basketId: string; itemId: string; quantityIndex: number; } & Action) => {
        const { basketId, itemId, quantityIndex } = action;
        return this.checkoutService.applicantImageLoad(basketId, itemId, quantityIndex).pipe(
          mergeMap((blob: Blob | undefined) => {
            return of(
              ImageActions.checkoutAPI_loadImageSuccess({
                basketId: basketId,
                itemId: itemId,
                quantityIndex: quantityIndex,
                blob,
              })
            );
          }),
          catchError((error) => {
            const apiError: ApiError | string =
              error?.response?.parsedBody || 'Image Load API call has failed';

            this.logger.logApiError(
              {
                apiError,
              },
              error
            );

            return of(
              ImageActions.checkoutAPI_loadImageFailure({
                error: apiError,
                itemId,
                quantityIndex,
                basketId,
                retryAction: action,
              })
            );
          })
        );
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private checkoutService: CheckoutService,
    @Inject(LOGGER) private logger: LoggingService
  ) {}
}
